import { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Copy, Download, Maximize2, X, ExternalLink, Printer } from "lucide-react";
import { ApplyPoster } from "@/components/ApplyPoster";

const PUBLIC_PATH = "/candidatura";

export const ApplyQrPanel = ({ isOpen, deadlineIso }) => {
  const url = `${window.location.origin}${PUBLIC_PATH}`;
  const hiddenRef = useRef(null);
  const [full, setFull] = useState(false);
  const [poster, setPoster] = useState(false);

  const downloadPng = () => {
    const canvas = hiddenRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = "qr-candidatura-growth-clubs.png";
    document.body.appendChild(a); a.click(); a.remove();
    toast.success("QR code descarregado (1024px)");
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado");
    } catch { toast.error("Não foi possível copiar"); }
  };

  return (
    <div className="kw-card p-5 flex flex-col md:flex-row gap-5 items-center" data-testid="apply-qr-panel">
      <div className="bg-white p-3 shrink-0">
        <QRCodeCanvas value={url} size={128} level="M" includeMargin={false} />
      </div>
      <div className="flex-1 min-w-0 w-full">
        <div className="kw-overline text-[10px]">Link público · QR para a apresentação</div>
        <div className="font-display font-black uppercase text-lg tracking-tight mt-1">
          Candidatura sem login
        </div>
        <div className="flex items-center gap-2 mt-2 bg-[#0F0F0F] border border-[#222] px-3 py-2">
          <code className="text-xs text-[#E50000] truncate flex-1" data-testid="apply-qr-url">{url}</code>
          <button onClick={copy} className="text-[#737373] hover:text-white shrink-0" title="Copiar link" data-testid="apply-qr-copy">
            <Copy className="w-3.5 h-3.5" />
          </button>
          <a href={url} target="_blank" rel="noreferrer" className="text-[#737373] hover:text-white shrink-0" title="Abrir">
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
        {!isOpen && (
          <div className="text-[10px] uppercase tracking-widest text-[#EAB308] mt-2">
            ⚠ O período está fechado — quem abrir o link vê "candidaturas fechadas"
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-3">
          <button onClick={downloadPng} className="px-3 py-2 border border-[#333] text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white hover:border-white inline-flex items-center gap-1.5 transition-colors" data-testid="apply-qr-download">
            <Download className="w-3.5 h-3.5" /> PNG para slide
          </button>
          <button onClick={() => setFull(true)} className="px-3 py-2 border border-[#E50000] text-[10px] uppercase tracking-widest font-bold text-[#E50000] hover:bg-[#E50000]/10 inline-flex items-center gap-1.5 transition-colors" data-testid="apply-qr-fullscreen">
            <Maximize2 className="w-3.5 h-3.5" /> Projetar em ecrã cheio
          </button>
          <button onClick={() => setPoster(true)} className="px-3 py-2 border border-[#EAB308] text-[10px] uppercase tracking-widest font-bold text-[#EAB308] hover:bg-[#EAB308]/10 inline-flex items-center gap-1.5 transition-colors" data-testid="apply-qr-poster">
            <Printer className="w-3.5 h-3.5" /> Cartaz para imprimir
          </button>
        </div>
      </div>

      {/* Hidden high-res canvas for download */}
      <div ref={hiddenRef} className="hidden">
        <QRCodeCanvas value={url} size={1024} level="M" includeMargin />
      </div>

      {full && <FullscreenQr url={url} onClose={() => setFull(false)} />}
      {poster && <ApplyPoster url={url} deadlineIso={deadlineIso} onClose={() => setPoster(false)} />}
    </div>
  );
};

function FullscreenQr({ url, onClose }) {
  return (
    <div className="fixed inset-0 z-[100] bg-[#0A0A0A] flex flex-col items-center justify-center p-8"
      data-testid="apply-qr-projection">
      <button onClick={onClose} className="absolute top-6 right-6 text-[#737373] hover:text-white" data-testid="apply-qr-projection-close">
        <X className="w-8 h-8" />
      </button>
      <div className="text-center max-w-4xl">
        <div className="text-[11px] md:text-sm uppercase tracking-[0.35em] text-[#737373]">Growth Clubs KW Lead</div>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter mt-3 leading-[0.95]">
          Candidata-te<br /><span className="text-[#E50000]">agora</span>
        </h2>
        <div className="bg-white p-6 inline-block mt-8">
          <QRCodeCanvas value={url} size={360} level="M" includeMargin={false} />
        </div>
        <div className="mt-6 text-lg md:text-2xl font-mono text-[#E50000] break-all">{url}</div>
        <div className="text-sm text-[#737373] mt-4 uppercase tracking-[0.2em]">
          Aponta a câmara · 3 minutos a preencher
        </div>
      </div>
    </div>
  );
}
